import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { ToastContainer } from "./components/ToastContainer";
import App from "./App";
import "./lib/i18n";
import "./index.css";

// Sentry error tracking — no-op when DSN is not set
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Theme initialization — prevent flash of wrong theme
const savedTheme = localStorage.getItem("carelogic-theme");
if (savedTheme === "dark" || (!savedTheme && window.matchMedia("(prefers-color-scheme: dark)").matches)) {
  document.documentElement.classList.add("dark");
}

// PostHog analytics is now lazy-loaded (src/lib/analytics.ts) — it dynamically
// imports posthog-js at idle on first capture/identify, keeping the ≈59KB gz
// vendor-analytics chunk off the critical boot path. No eager init here.

// Register service worker for offline support
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Dev-only: warn when a card's content overflows its frame horizontally.
// Production bundles tree-shake this out via the import.meta.env.DEV guard.
if (import.meta.env.DEV) {
  import("./lib/overflowDetector").then((m) => m.startOverflowDetector()).catch(() => {});
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <App />
        <ToastContainer />
      </ToastProvider>
    </AuthProvider>
  </StrictMode>
);

// Service worker disabled — was causing stale cache issues on deploy
